import React, { useRef, useState, useEffect } from "react";
import { Modal, Button } from "react-bootstrap";
import { FormattedMessage } from "react-intl";
import SignatureCanvas from "react-signature-canvas";
import "./agreement-sign-form.css";
import { observer } from "mobx-react";
import CheckAgreementPasswordPopupHOC from "../hoc/check-agreement-password-popup";
import CheckAgreementCard from "./check-agreement-card";
import ReactQuill from "react-quill";
import { formatMessage } from "../../../translations/format-message";

interface IProps {
  submitHandler: (values: any) => void;
  modalClosed: () => void;
  agreementContent: string;
  showCheckAgreementPasswordPopup: boolean;
  checkAgreementPasswordPopupToggleHandler: () => void;
}

const AgreementSignForm = (props: IProps) => {
  const sigCanvasRef = useRef<SignatureCanvas | null>(null);
  const canvasWrapperRef = useRef<HTMLDivElement | null>(null);

  const [isDrawing, setIsDrawing] = useState(false);
  const [isCancelAction, setIsCancelAction] = useState(false);
  const [canvasSize, setCanvasSize] = useState({ width: 0, height: 0 });

  // Resize canvas to fit container
  useEffect(() => {
    if (canvasWrapperRef.current) {
      const { clientWidth, clientHeight } = canvasWrapperRef.current;
      setCanvasSize({ width: clientWidth, height: Math.max(clientHeight - 50, 0), });
    }
    const handleResize = () => {
      if (canvasWrapperRef.current) {
        const { clientWidth, clientHeight } = canvasWrapperRef.current;
        setCanvasSize({ width: clientWidth, height: Math.max(clientHeight - 50, 0) });
      }
    };
    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, []);

  const handleClear = () => {
    sigCanvasRef.current?.clear();
  };

  const openCheckAgreementPasswordPopup = (isActionCancel: boolean) => {
    setIsCancelAction(isActionCancel);
    props.checkAgreementPasswordPopupToggleHandler();
  };

  const closeCheckAgreementPasswordPopup = () => {
    if (!isCancelAction) {
      if (sigCanvasRef.current) {
        const signatureData = sigCanvasRef.current.toDataURL("image/png");
        props.submitHandler(signatureData);
      }
    }
    setIsCancelAction(false);
    props.modalClosed();
    props.checkAgreementPasswordPopupToggleHandler();
  };

  return (
    <>
      {props.showCheckAgreementPasswordPopup && (
        <CheckAgreementCard
          submitHandler={closeCheckAgreementPasswordPopup}
          modalClosed={props.checkAgreementPasswordPopupToggleHandler}
        />
      )}
      <Modal className="agreemenFullpage-modal" show={true} size="lg" centered>
        <div className="row g-0">
          <div className="col-12">
            <ReactQuill
              value={props.agreementContent}
              modules={{ toolbar: false }}
              theme="snow"
              readOnly={true}
            />
          </div>
          <div className="col-12 canvas-sign" ref={canvasWrapperRef}>
            <div className="border">
              <SignatureCanvas
                penColor="black"
                minWidth={3}
                maxWidth={6.5}
                velocityFilterWeight={0.7}
                canvasProps={{
                  width: canvasSize.width,
                  height: canvasSize.height,
                  className: isDrawing ? "sigCanvas drawing" : "sigCanvas",
                }}
                onBegin={() => setIsDrawing(true)}
                onEnd={() => setIsDrawing(false)}
                ref={sigCanvasRef}
              />
            </div>
            <button onClick={handleClear} className="btn btn-primary mt-2 w-100">
              {formatMessage("clear")}
            </button>
          </div>
          <div className="justify-content-center p-3 d-flex gap-3">
            <Button
              variant="danger"
              type="button"
              onClick={() => openCheckAgreementPasswordPopup(true)}
            >
              <i className="bi bi-x-lg me-1"></i>
              <FormattedMessage id="close" />
            </Button>
            <Button
              variant="primary"
              type="button"
              onClick={() => openCheckAgreementPasswordPopup(false)}
            >
              <i className="bi bi-check-lg me-1"></i>
              <FormattedMessage id="button_save" />
            </Button>
          </div>
        </div>
      </Modal>
    </>
  );
};

export default CheckAgreementPasswordPopupHOC(observer(AgreementSignForm));
